import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEqual, isNull, keyBy } from 'lodash-es';
import { useObservable, useObservableState } from 'observable-hooks';
import PropTypes from 'prop-types';
import { useMemo, useRef } from 'react';
import { Controller, useController } from 'react-hook-form';
import { combineLatest, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { array, object, string } from 'yup';
import mergeRefs from 'merge-refs';
import { OverridableSelect } from '../../../form/OverridableSelect';
import classNames from '../../../../utils/classNames';
import { replaceDistanceUnit, roundToDistanceUnitDigits } from '../../../../utils/formatting';
import { applyNamingConvention } from '../../../../utils/naming-conventions';
import { PLACEHOLDER_ROW_STRATEGY, RowManager } from '../../../../utils/row-manager';
import Input, { INPUT_SIZE } from '../../../form/Input';
import TextArea from '../../../form/TextArea';
import {
    defaultPropTypes,
    fromReactHookFormWatch,
    useOverrideState,
    useSharedFormContext,
    useWatchObservable,
} from '../../utils';
import { buildYupSchema } from '../../validation';
import FormFieldContainer from '../FormFieldContainer';
import { GenericRowField, inlineFieldGroupClasses } from '../genericRowField/GenericRowField';
import { fieldTypeToInputType, isNumberType, OverridableInput } from '../InputField';
import { AlarmRowManager } from './alarm-row-manager';
import { createAlarmRecipientPlaceholder, createInvalidEmailOrPhoneText } from './utils';
import {
    getValidEmail,
    isEmailValid,
    isPhoneNumberValid,
    normalizeInputText,
    tryParsePhoneNumber,
} from '../../../../utils/validators';
import { NestedFieldsRegistry, useNestedFields } from '../../nested-field-registry';
import { DATA_TYPE } from '../../../../graph/enums';
import { useProfile } from '../../../../profile';

const borderColorClasses = ['border-input-green', 'border-input-orange', 'border-input-red'];

function getIdentity(recipient) {
    return recipient.email ?? recipient.phoneNumber;
}

export function shouldIgnoreAutofillCloneFromFocusedRow({
    rows,
    focusedRowIndex,
    targetRowIndex,
    nextValue,
}) {
    const previousRow = rows[targetRowIndex];
    const focusedRow = focusedRowIndex !== null ? rows[focusedRowIndex] : null;
    const previousValue = previousRow ? getIdentity(previousRow) ?? '' : '';
    const focusedRowValue = focusedRow ? getIdentity(focusedRow) ?? '' : '';

    return (
        focusedRowIndex !== null &&
        focusedRowIndex !== targetRowIndex &&
        !previousValue &&
        !!nextValue &&
        focusedRowValue === nextValue
    );
}

function AlarmFieldInput({
    id,
    placeholder,
    ariaLabel,
    fieldType,
    fieldName,
    value,
    onChange,
    nestedFieldRef,
    error,
}) {
    const ref = useRef(null);
    const profile = useProfile();
    const conditions$ = useWatchObservable([
        'atopEnabled',
        'buildingLevel',
        'atopFlatLevel',
        'flatLevel',
    ]);

    const [helpers] = useObservableState(
        () =>
            fieldName === 'alarmLevel'
                ? conditions$.pipe(
                      map(({ atopEnabled, buildingLevel, atopFlatLevel, flatLevel }) => [
                          // Vtop appears when the line is considered "flat".
                          {
                              show: buildingLevel === 'unspecified',
                              level: flatLevel,
                              id: `${id}_v-top`,
                              helpText: gettext('ALARM_LEVEL_VTOP'),
                              unit: 's',
                          },
                          // Atop appears when atop_enabled is True.
                          {
                              show: atopEnabled,
                              level: atopFlatLevel,
                              id: `${id}_a-top`,
                              helpText: gettext('ALARM_LEVEL_ATOP'),
                              unit: 's^2',
                          },
                      ])
                  )
                : of([]),
        []
    );

    return (
        <>
            {fieldName === 'smsTemplate' ? (
                <TextArea
                    ref={mergeRefs(ref, nestedFieldRef)}
                    id={id}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    aria-label={ariaLabel}
                    maxLength={80}
                    error={Boolean(error)}
                />
            ) : (
                <Input
                    ref={mergeRefs(ref, nestedFieldRef)}
                    id={id}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    aria-label={ariaLabel}
                    type={fieldTypeToInputType(fieldType)}
                    error={error}
                />
            )}
            {helpers
                .filter((helper) => helper.show)
                .map(({ level, id: helperId, helpText, unit }) => (
                    <div className="my-2" key={helperId}>
                        <Input
                            // `value` is a percentage, so we evaluate (value/100 ) * level.
                            value={
                                value
                                    ? roundToDistanceUnitDigits(
                                          Number(value) * level * 0.01,
                                          'flatLevel',
                                          profile.distanceUnit
                                      )
                                    : 0
                            }
                            onChange={(e) => {
                                // Convert the value back to a percentage.
                                const v = (e.target.valueAsNumber * 100) / level;
                                onChange({
                                    ...e,
                                    target: { ...ref.current, value: v, valueAsNumber: v },
                                });
                            }}
                            type="number"
                            id={helperId}
                        />
                        <label className="text-[11px]" htmlFor={helperId}>
                            {interpolate(applyNamingConvention(helpText), [
                                `${replaceDistanceUnit('mm', profile.distanceUnit)}/${unit}`,
                                // 1% of the flat level is the min value.
                                roundToDistanceUnitDigits(
                                    level / 100,
                                    'flatLevel',
                                    profile.distanceUnit
                                ),
                            ])}
                        </label>
                    </div>
                ))}
        </>
    );
}

AlarmFieldInput.propTypes = {
    placeholder: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    onChange: PropTypes.func.isRequired,
    ariaLabel: PropTypes.string.isRequired,
    fieldType: PropTypes.string.isRequired,
    fieldName: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    nestedFieldRef: PropTypes.func.isRequired,
    error: PropTypes.bool,
};

function AlarmLevelSettingsField({ alarm }) {
    const { name, title, limit, placeholder, keyTemplate } = alarm;
    const overrideName = `notificationConfig.${name}`;
    const profile = useProfile();
    const { overridable, overridden } = useOverrideState(overrideName);
    const disabled = overridable && !overridden;
    const { formController } = useSharedFormContext();

    const showColors = alarm.showColors ?? false;

    const emptyRow = useMemo(
        () => Object.fromEntries(alarm.fields.map((field) => [field.name, field.default])),
        [alarm.fields]
    );

    const extraManagerConfig = useMemo(
        () => ({ keyTemplate, formControl: formController.rhfProps.formControl }),
        [keyTemplate, formController.rhfProps.formControl]
    );

    const nestedFields = useNestedFields(overrideName);

    const {
        field: { onChange, value },
        fieldState: { error },
    } = useController({
        name: overrideName,
        rules: {
            buildValidationSchema() {
                return array(
                    buildYupSchema(keyBy(alarm.fields, 'name'), {
                        distanceUnit: profile.distanceUnit,
                    }).test('particleType', null, function checkAirParticle(particleAlarm) {
                        const values = this.options.context.getValues();
                        const sensorParticleType = values.particleType;
                        let hasError = false;
                        let PM10Error = false;

                        // Check if the device is set to PM10 which allows the
                        // calculation of PM2.5.
                        if (sensorParticleType === DATA_TYPE.PM10) {
                            PM10Error = ![DATA_TYPE.PM10, DATA_TYPE.PM2P5].includes(
                                particleAlarm.particleType
                            );
                        } else {
                            hasError = sensorParticleType !== particleAlarm.particleType;
                        }
                        if (hasError || PM10Error) {
                            return this.createError({
                                path: `${this.path}.particleType`,
                                message: hasError
                                    ? gettext('PARTICLE_TYPE_INCOMPATIBLE')
                                    : gettext('PM10_INCOMPATIBLE'),
                            });
                        }
                        return true;
                    })
                );
            },
        },
    });

    const fields = useMemo(
        () =>
            alarm.fields.map((field, index) => ({
                name: field.name,
                label: field.label,
                helpText: field.helpText,
                render(props) {
                    // Add 1 to make it a human index.
                    // eslint-disable-next-line react/prop-types
                    const humanIndex = props.index + 1;
                    const ariaLabel = `${field.label} ${humanIndex}`;

                    if (field.type === 'char' || isNumberType(field.type)) {
                        const { ref } = nestedFields.register(
                            // eslint-disable-next-line react/prop-types
                            `[${props.index}].${field.name}`
                        );

                        return (
                            <>
                                <AlarmFieldInput
                                    {...props}
                                    nestedFieldRef={ref}
                                    humanIndex={humanIndex}
                                    // Only add the alarm level placeholder to the title input.
                                    placeholder={
                                        field.name === 'title'
                                            ? placeholder.replace('{index}', humanIndex)
                                            : null
                                    }
                                    ariaLabel={ariaLabel}
                                    fieldType={field.type}
                                    fieldName={field.name}
                                    // `error` comes from GenericRowField’s render function.
                                    // ESLint warns here because `props` in this render function,
                                    // has no declared shape.
                                    // eslint-disable-next-line react/prop-types
                                    error={props.error}
                                />
                                {showColors && index === 0 && (
                                    <div
                                        className={classNames(
                                            'border',
                                            // eslint-disable-next-line react/prop-types
                                            borderColorClasses[props.index]
                                        )}
                                    ></div>
                                )}
                            </>
                        );
                    }

                    if (field.type === 'choice') {
                        return (
                            <OverridableSelect
                                {...props}
                                data={field.options}
                                aria-label={ariaLabel}
                                overridable={overridable}
                                overridden={overridden}
                                overrideName={overrideName}
                            />
                        );
                    }

                    return new Error(
                        `AlarmSettingsField could not find field input for type '${field.type}'.`
                    );
                },
            })),
        [alarm.fields, showColors, placeholder, nestedFields, overridable, overridden, overrideName]
    );

    return (
        <FormFieldContainer
            fieldOptions={{
                name: overrideName,
                settings: {
                    // Although the label is hidden, it is used by the
                    // aria-label of the override checkbox.
                    label: title,
                },
            }}
            showLabel={false}
        >
            <GenericRowField
                name={overrideName}
                fields={fields}
                emptyRow={emptyRow}
                maxRows={limit ?? Infinity}
                value={value}
                error={error}
                onChange={onChange}
                disabled={disabled}
                Manager={AlarmRowManager}
                extraConfig={extraManagerConfig}
            />
        </FormFieldContainer>
    );
}

AlarmLevelSettingsField.propTypes = {
    alarm: PropTypes.shape({
        name: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        limit: PropTypes.number,
        placeholder: PropTypes.string.isRequired,
        keyTemplate: PropTypes.string.isRequired,
        showColors: PropTypes.bool,
        fields: PropTypes.array.isRequired,
    }),
};

function AlarmRecipientField({
    index,
    disabled,
    configuredAlarms,
    row,
    manager,
    error,
    overrideName,
    nestedFields,
    onFocus,
    onValueChange,
}) {
    const humanIndex = index + 1;
    const enabledAlarmsError = error?.enabledAlarms;
    const identityError = error?.identity;
    const inputName = `${overrideName}-row-${index}`;

    const { ref: identifyRef } = nestedFields.register(`[${index}].identity`);
    const { ref: enabledAlarmsRef } = nestedFields.register(`[${index}].enabledAlarms`);

    return (
        <tr>
            <td className="w-56 pr-3">
                {/*
                Because the values of registered fields that are disabled do
                not come back with `getValues()` or `watch()` we have to do a
                hack here that creates an extra fake disabled field when disabled.
                This allows us to simply hide the original field and keep everything
                working as expected.
                */}
                <OverridableInput
                    ref={identifyRef}
                    type="text"
                    placeholder={createAlarmRecipientPlaceholder()}
                    aria-label={interpolate(
                        gettext('RECIPIENT_INPUT_INDEX'),
                        { index: humanIndex },
                        true
                    )}
                    size={INPUT_SIZE.MEDIUM}
                    error={!!identityError}
                    disabled={disabled}
                    overrideName={overrideName}
                    name={inputName}
                    value={getIdentity(row) ?? ''}
                    onFocus={() => onFocus(index)}
                    onChange={(e) => onValueChange(index, e.target.value ?? '')}
                />
                {identityError && (
                    <span className="text-form-warning text-xs">{identityError.message}</span>
                )}
            </td>
            {configuredAlarms.map(([key, title]) => (
                <td className={'w-10 p-0 text-center align-top'} key={key}>
                    <div
                        className={`flex h-10 items-center justify-center border-l border-[#ccc] ${
                            enabledAlarmsError && 'bg-form-warning'
                        }`}
                    >
                        <OverridableInput
                            type="checkbox"
                            ref={enabledAlarmsRef}
                            aria-label={interpolate(
                                gettext('RECIPIENT_CHECKBOX_INDEX'),
                                {
                                    title,
                                    index: humanIndex,
                                },
                                true
                            )}
                            disabled={disabled}
                            overrideName={overrideName}
                            checked={row.enabledAlarms.includes(key)}
                            onChange={(e) => {
                                let newValue = [...row.enabledAlarms];

                                if (e.target.checked) {
                                    !newValue.includes(key) && newValue.push(key);
                                } else {
                                    newValue.includes(key) &&
                                        (newValue = newValue.filter((alarm) => alarm !== key));
                                }

                                manager.changeRow(index, 'enabledAlarms', newValue);
                            }}
                        />
                    </div>
                </td>
            ))}
        </tr>
    );
}

AlarmRecipientField.propTypes = {
    index: PropTypes.number.isRequired,
    disabled: PropTypes.bool.isRequired,
    configuredAlarms: PropTypes.array.isRequired,
    row: PropTypes.shape({
        email: PropTypes.string,
        phoneNumber: PropTypes.string,
        enabledAlarms: PropTypes.array.isRequired,
    }).isRequired,
    manager: PropTypes.instanceOf(RowManager).isRequired,
    error: PropTypes.object,
    overrideName: PropTypes.string.isRequired,
    nestedFields: PropTypes.instanceOf(NestedFieldsRegistry),
    onFocus: PropTypes.func.isRequired,
    onValueChange: PropTypes.func.isRequired,
};

const emptyRecipientRow = {
    enabledAlarms: [],
    email: null,
    phoneNumber: null,
    webhook: null,
};

function isValidContactInfo(test, validationFunction) {
    return isNull(test) || test === '' || validationFunction(test);
}

function useConfiguredAlarms(alarms) {
    const { formController } = useSharedFormContext();

    const configuredAlarms$ = useObservable(
        (inputs$) =>
            inputs$.pipe(
                switchMap(([incomingFormControl, incomingAlarms]) =>
                    combineLatest([
                        of([['measuring_point_administrator', gettext('ORIGINAL_CREATOR')]]),
                        // Per alarm type, start watching the configured alarm rows.
                        ...incomingAlarms.map((alarm) =>
                            fromReactHookFormWatch(
                                incomingFormControl,
                                `notificationConfig.${alarm.name}`
                            ).pipe(
                                map((alarmRows) =>
                                    alarmRows.map((alarmField, index) => [
                                        alarmField.key,
                                        alarmField.title === '' || isNull(alarmField.title)
                                            ? alarm.placeholder.replace('{index}', index + 1)
                                            : alarmField.title,
                                    ])
                                )
                            )
                        ),
                        fromReactHookFormWatch(incomingFormControl, 'ldenGuideline').pipe(
                            map((ldenGuideline) =>
                                !ldenGuideline || ldenGuideline === 'LdenGuidelineNone'
                                    ? []
                                    : [
                                          ['lden_alarm', gettext('LDEN_ALARM')],
                                          ['lden_warning', gettext('LDEN_WARNING')],
                                          ['lden_lmax_1', gettext('LDEN_LMAX')],
                                          ['lden_leq_1', gettext('LDEN_LEQ')],
                                      ]
                            )
                        ),
                    ])
                ),
                map((a) => a.flat()),
                distinctUntilChanged(isEqual)
            ),
        [formController.rhfProps.formControl, alarms]
    );

    return useObservableState(configuredAlarms$, []);
}

function AlarmRecipientsField({ fieldOptions }) {
    const {
        settings: { alarms },
    } = fieldOptions;
    const subName = 'recipients';
    const overrideName = `notificationConfig.${subName}`;
    const { disabled } = useOverrideState(overrideName);
    const nestedFields = useNestedFields(overrideName);
    const configuredAlarms = useConfiguredAlarms(alarms);
    const focusedRowIndexRef = useRef(null);

    const {
        field: { value: incomingValue, onChange },
        fieldState: { error },
    } = useController({
        name: overrideName,
        rules: {
            buildValidationSchema() {
                return array(
                    object({
                        email: string()
                            .nullable()
                            .transform((value) =>
                                typeof value === 'string' ? normalizeInputText(value) : value
                            ),
                        phoneNumber: string()
                            .nullable()
                            .transform((value) => {
                                const normalizedValue =
                                    typeof value === 'string' ? normalizeInputText(value) : value;
                                const phoneNumber = tryParsePhoneNumber(normalizedValue);
                                // We only want to transform if the phone number is
                                // present and valid.
                                if (phoneNumber && phoneNumber.isValid()) {
                                    // Returns the phone number in E.164 format.
                                    return phoneNumber.number;
                                }

                                // If no transformation is needed, return the original
                                // value untouched.
                                return normalizedValue;
                            }),
                        enabledAlarms: array().when(['email', 'phoneNumber'], {
                            is(email, phoneNumber) {
                                return email ?? phoneNumber;
                            },
                            then(schema) {
                                return schema.min(
                                    1,
                                    /* Not translated as this message is not visible to
                                        the user. */
                                    'There must be at least one enabled alarm if email or phone number is provided.'
                                );
                            },
                        }),
                    }).test('identity', null, function validateIdentity(value) {
                        const { email, phoneNumber, enabledAlarms } = value;
                        // Get the index out of the path so we can assign the error to the
                        // proper entry.
                        const index = this.path.replace('["', '').split('[')[1].replace(']', '');

                        if (enabledAlarms.length > 0 && !email && !phoneNumber) {
                            return this.createError({
                                path: `${overrideName}.${index}.identity`,
                                message: gettext('FIELD_REQUIRED_VALIDATION'),
                            });
                        }

                        if (
                            isValidContactInfo(email, isEmailValid) &&
                            isValidContactInfo(phoneNumber, isPhoneNumberValid)
                        ) {
                            return true;
                        }

                        return this.createError({
                            path: `${overrideName}.${index}.identity`,
                            message: createInvalidEmailOrPhoneText(),
                        });
                    })
                );
            },
        },
    });
    const { rows, manager } = RowManager.useRowManager(
        incomingValue,
        (value) => {
            onChange(value);
        },
        Infinity,
        emptyRecipientRow,
        {
            placeholderRowStrategy: PLACEHOLDER_ROW_STRATEGY.ADD_WHEN_LAST_ROW_IS_DIRTY,
        }
    );

    function handleRowFocus(index) {
        focusedRowIndexRef.current = index;
    }

    function handleValueChange(index, nextValue) {
        if (
            shouldIgnoreAutofillCloneFromFocusedRow({
                rows,
                focusedRowIndex: focusedRowIndexRef.current,
                targetRowIndex: index,
                nextValue,
            })
        ) {
            return;
        }

        const normalized =
            (typeof nextValue === 'string' ? normalizeInputText(nextValue) : '') || null;

        const email = normalized ? getValidEmail(normalized) : null;
        const phoneNumber = !email && normalized ? normalized : null;

        focusedRowIndexRef.current = index;
        manager.changeRow(index, { email, phoneNumber });
    }

    return (
        <FormFieldContainer
            fieldOptions={{
                name: overrideName,
                settings: {
                    // Although the label is hidden, it is used by the aria-label of the override
                    // checkbox.
                    label: gettext('ALARM_RECIPIENTS'),
                },
            }}
            showLabel={false}
            className="inline-block"
            helpText={gettext('EMAIL_OR_PHONE_HINT')}
        >
            <div className="mt-24 text-sm">{gettext('ALARM_RECIPIENTS')}</div>
            {/* The ID is needed for the walkthrough. */}
            <table id="alarmRecipientSettings" className="-mt-[120px]">
                <thead>
                    <tr>
                        <th className="p-0"></th>
                        {configuredAlarms.map(([key, title]) => (
                            <th className="relative h-[120px] p-0" key={key}>
                                <div className="absolute bottom-0 left-0">
                                    <span className="absolute bottom-0 right-0 w-[165px] origin-bottom-right rotate-45 truncate border-b border-[#ccc] pb-1 pr-2 text-right leading-none">
                                        {title}
                                    </span>
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => (
                        <AlarmRecipientField
                            row={row}
                            index={index}
                            key={index}
                            configuredAlarms={configuredAlarms}
                            manager={manager}
                            disabled={disabled}
                            error={error?.[index]}
                            overrideName={overrideName}
                            nestedFields={nestedFields}
                            onFocus={handleRowFocus}
                            onValueChange={handleValueChange}
                        />
                    ))}
                </tbody>
            </table>

            <div>
                <button
                    type="button"
                    onClick={() => !disabled && manager.addRow()}
                    aria-label={gettext('ADD_EMPTY_FIELD')}
                    className="cursor-pointer"
                >
                    <FontAwesomeIcon
                        icon={faPlusCircle}
                        className="text-success"
                        title={gettext('ADD_EMPTY_FIELD')}
                    />
                </button>
            </div>
        </FormFieldContainer>
    );
}

AlarmRecipientsField.propTypes = {
    ...defaultPropTypes,
};

function EmailDelayField({ fieldOptions }) {
    const { name } = fieldOptions;
    const subName = 'emailDelay';
    const overrideName = `${name}.${subName}`;
    const { disabled } = useOverrideState(overrideName);

    return (
        <Controller
            name={overrideName}
            fieldOptions={fieldOptions}
            rules={{
                // Please note: these are settings that validate.js:buildYupSchema reacts on.
                type: 'integer',
                minValue: 10,
                maxValue: 24 * 60 * 60,
            }}
            render={({ field, fieldState: { error } }) => (
                <FormFieldContainer
                    fieldOptions={{
                        name: overrideName,
                        settings: {
                            label: gettext('EMAIL_DELAY_LABEL'),
                        },
                    }}
                    inline={true}
                >
                    <OverridableInput
                        overrideName={overrideName}
                        id={`id_${overrideName}`}
                        type="number"
                        disabled={disabled}
                        error={error}
                        {...field}
                    />
                </FormFieldContainer>
            )}
        />
    );
}

EmailDelayField.propTypes = {
    ...defaultPropTypes,
};

export function NotificationConfigField({ fieldOptions }) {
    const {
        settings: { alarms },
    } = fieldOptions;

    return (
        <div className="flex flex-col gap-y-2.5">
            {alarms.map((alarm, index) => (
                // The ID is needed for the walkthrough.
                <div key={alarm.name} id={index === 0 ? 'alarmLevels' : null}>
                    <h3 className="text-black">{alarm.title}</h3>
                    <AlarmLevelSettingsField alarm={alarm} />
                </div>
            ))}
            <h3 className="text-black">{gettext('ALARM_RECEPTION_SETTINGS')}</h3>
            <div className={inlineFieldGroupClasses}>
                <AlarmRecipientsField fieldOptions={fieldOptions} />
                <EmailDelayField fieldOptions={fieldOptions} />
            </div>
        </div>
    );
}

NotificationConfigField.propTypes = defaultPropTypes;
